import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { NotificationService } from '../../services/notification.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-medical-records',
  templateUrl: './medical-records.component.html',
  styleUrls: ['./medical-records.component.scss']
})
export class MedicalRecordsComponent implements OnInit, OnDestroy {
  result: any = { items: [], totalCount: 0, page: 1, pageSize: 20 };
  loading = false;
  professionalId = 0;
  selectedProfessional: any = null;

  filter: any = { page: 1, pageSize: 20, search: '', startDate: '', endDate: '', patientId: null };

  // Modal principal
  showModal = false;
  saving = false;
  editingId: number | null = null;
  form: any = {
    patientId: null, specialty: '', appointmentType: 1, modality: 1,
    chiefComplaint: '', medicalHistory: '', relevantHistory: '',
    assessment: '', diagnosis: '', observations: ''
  };

  // Seleção de paciente
  patients: any[] = [];
  patientSearchTerm = '';
  showPatientDropdown = false;
  selectedPatient: any = null;

  // Timeline de evoluções
  evolutions: any[] = [];
  showEvolutionModal = false;
  evolutionForm: any = { evolutionDate: '', notes: '' };
  savingEvolution = false;

  // Anexos
  attachments: any[] = [];
  uploadingFile = false;
  deletingAttachmentId: number | null = null;

  // Enums
  appointmentTypes = [
    { value: 1, label: 'Consulta' },
    { value: 2, label: 'Retorno' },
    { value: 3, label: 'Urgência' },
    { value: 4, label: 'Avaliação' }
  ];
  modalities = [
    { value: 1, label: 'Presencial' },
    { value: 2, label: 'Telemedicina' }
  ];

  // Datepicker - Filter startDate
  showStartDatePicker = false;
  startPickerCurrentDate = new Date();
  startPickerDays: Date[] = [];
  startPickerTop = '0px';
  startPickerLeft = '0px';
  startDateDisplay = ''; // dd/MM/yyyy format
  weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  @ViewChild('startDateInput', { read: ElementRef }) startDateInput?: ElementRef;

  // Datepicker - Filter endDate
  showEndDatePicker = false;
  endPickerCurrentDate = new Date();
  endPickerDays: Date[] = [];
  endPickerTop = '0px';
  endPickerLeft = '0px';
  endDateDisplay = ''; // dd/MM/yyyy format

  @ViewChild('endDateInput', { read: ElementRef }) endDateInput?: ElementRef;

  // Datepicker - Evolution Date
  showEvolutionDatePicker = false;
  evolutionPickerCurrentDate = new Date();
  evolutionPickerDays: Date[] = [];
  evolutionPickerTop = '0px';
  evolutionPickerLeft = '0px';
  evolutionDateDisplay = ''; // dd/MM/yyyy format

  @ViewChild('evolutionDateInput', { read: ElementRef }) evolutionDateInput?: ElementRef;

  private searchInput$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private api: ApiService,
    private profState: ProfessionalStateService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$.pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(term => { this.filter.search = term; this.filter.page = 1; this.load(); })
    );

    this.subs.add(
      this.profState.selected$.subscribe(p => {
        if (p) {
          this.professionalId = p.id;
          this.selectedProfessional = p;
          this.load();
          this.loadPatients();
        }
      })
    );
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  load(): void {
    this.loading = true;
    this.api.getMedicalRecords(this.professionalId, this.filter).subscribe({
      next: r => { this.result = r; this.loading = false; },
      error: () => this.loading = false
    });
  }

  loadPatients(): void {
    this.api.getPatients(this.professionalId, { pageSize: 100 }).subscribe({
      next: r => this.patients = r.items,
      error: () => {}
    });
  }

  onSearch(term: string): void { this.searchInput$.next(term); }
  onPageChange(page: number): void { this.filter.page = page; this.load(); }
  onSizeChange(size: number): void { this.filter.pageSize = size; this.filter.page = 1; this.load(); }

  openModal(record?: any): void {
    this.editingId = record?.id ?? null;
    if (record) {
      this.api.getMedicalRecord(this.professionalId, record.id).subscribe({
        next: detail => {
          this.form = { ...detail };
          this.evolutions = detail.evolutions || [];
          this.attachments = detail.attachments || [];
          this.selectedPatient = { id: detail.patientId, name: detail.patientName };
        }
      });
    } else {
      this.resetForm();
    }
    this.showModal = true;
  }

  resetForm(): void {
    this.form = {
      patientId: null,
      specialty: this.selectedProfessional?.specialty || '',
      appointmentType: 1,
      modality: 1,
      chiefComplaint: '', medicalHistory: '', relevantHistory: '',
      assessment: '', diagnosis: '', observations: ''
    };
    this.evolutions = [];
    this.attachments = [];
    this.selectedPatient = null;
  }

  selectPatient(patient: any): void {
    this.selectedPatient = patient;
    this.form.patientId = patient.id;
    this.showPatientDropdown = false;
  }

  save(): void {
    if (!this.form.patientId) { this.notification.warning('Campo obrigatório', 'Selecione um paciente'); return; }
    if (!this.form.specialty?.trim()) { this.notification.warning('Campo obrigatório', 'Especialidade é obrigatória'); return; }

    this.saving = true;
    const payload = { ...this.form, professionalId: this.professionalId };
    const op = this.editingId
      ? this.api.updateMedicalRecord(this.professionalId, this.editingId, payload)
      : this.api.createMedicalRecord(this.professionalId, payload);

    op.subscribe({
      next: () => { this.saving = false; this.showModal = false; this.load(); },
      error: () => { this.saving = false; this.notification.error('Erro', 'Erro ao salvar prontuário'); }
    });
  }

  delete(record: any): void {
    if (!confirm(`Excluir prontuário de ${record.patientName}?`)) return;
    this.api.deleteMedicalRecord(this.professionalId, record.id).subscribe({
      next: () => this.load(),
      error: () => this.notification.error('Erro', 'Erro ao excluir prontuário')
    });
  }

  // Timeline
  openEvolutionModal(): void {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    this.evolutionForm = {
      evolutionDate: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${now.toTimeString().slice(0, 5)}`,
      notes: ''
    };
    this.evolutionDateDisplay = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`;
    this.showEvolutionModal = true;
  }

  saveEvolution(): void {
    if (!this.evolutionForm.notes?.trim()) { this.notification.warning('Campo obrigatório', 'Preencha as anotações'); return; }
    this.savingEvolution = true;
    this.api.addEvolution(this.professionalId, this.editingId!, this.evolutionForm).subscribe({
      next: ev => {
        this.evolutions = [ev, ...this.evolutions];
        this.savingEvolution = false;
        this.showEvolutionModal = false;
      },
      error: () => { this.savingEvolution = false; this.notification.error('Erro', 'Erro ao adicionar evolução'); }
    });
  }

  // Upload
  onFileSelect(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    this.uploadingFile = true;
    this.api.uploadAttachment(this.professionalId, this.editingId!, file).subscribe({
      next: attachment => {
        this.attachments = [attachment, ...this.attachments];
        this.uploadingFile = false;
      },
      error: () => { this.uploadingFile = false; this.notification.error('Erro', 'Erro ao fazer upload do arquivo'); }
    });
  }

  deleteAttachment(attachment: any): void {
    if (!confirm(`Excluir ${attachment.fileName}?`)) return;
    this.deletingAttachmentId = attachment.id;
    this.api.deleteAttachment(this.professionalId, this.editingId!, attachment.id).subscribe({
      next: () => {
        this.attachments = this.attachments.filter(a => a.id !== attachment.id);
        this.deletingAttachmentId = null;
      },
      error: () => { this.deletingAttachmentId = null; this.notification.error('Erro', 'Erro ao excluir anexo'); }
    });
  }

  exportPdf(): void {
    if (!this.editingId) return;
    this.api.exportMedicalRecordPdf(this.professionalId, this.editingId).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `prontuario_${this.editingId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.notification.error('Erro', 'Erro ao gerar PDF do prontuário')
    });
  }

  appointmentTypeLabel(type: number): string {
    return this.appointmentTypes.find(t => t.value === type)?.label || '-';
  }

  modalityLabel(mod: number): string {
    return this.modalities.find(m => m.value === mod)?.label || '-';
  }

  fmtDate(d?: string): string {
    return d ? new Date(d).toLocaleDateString('pt-BR') : '-';
  }

  // ═══════════════════════════════════════════════════════════════
  // DATEPICKER - FILTER START DATE
  // ═══════════════════════════════════════════════════════════════

  toggleStartDatePicker(): void {
    this.showStartDatePicker = !this.showStartDatePicker;
    if (this.showStartDatePicker) {
      if (this.filter.startDate) {
        const parts = this.startDateDisplay.split('/');
        if (parts.length === 3) {
          this.startPickerCurrentDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        }
      } else {
        this.startPickerCurrentDate = new Date();
      }
      this.generateStartPickerCalendar();

      setTimeout(() => {
        if (this.startDateInput?.nativeElement) {
          const rect = this.startDateInput.nativeElement.getBoundingClientRect();
          this.startPickerTop = `${rect.bottom + 8}px`;
          this.startPickerLeft = `${rect.left}px`;
        }
      }, 50);
    }
  }

  generateStartPickerCalendar(): void {
    const year = this.startPickerCurrentDate.getFullYear();
    const month = this.startPickerCurrentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const days: Date[] = [];

    for (let i = startDay - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push(new Date(year, month + 1, d));
    }
    this.startPickerDays = days;
  }

  startPickerPreviousMonth(): void {
    this.startPickerCurrentDate = new Date(
      this.startPickerCurrentDate.getFullYear(),
      this.startPickerCurrentDate.getMonth() - 1,
      1
    );
    this.generateStartPickerCalendar();
  }

  startPickerNextMonth(): void {
    this.startPickerCurrentDate = new Date(
      this.startPickerCurrentDate.getFullYear(),
      this.startPickerCurrentDate.getMonth() + 1,
      1
    );
    this.generateStartPickerCalendar();
  }

  getStartPickerMonthYear(): string {
    return this.startPickerCurrentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  startPickerIsCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.startPickerCurrentDate.getMonth();
  }

  startPickerIsToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  startPickerIsSelected(date: Date): boolean {
    if (!this.filter.startDate) return false;
    const parts = this.startDateDisplay.split('/');
    if (parts.length !== 3) return false;
    return date.getDate() === parseInt(parts[0]) &&
           date.getMonth() === parseInt(parts[1]) - 1 &&
           date.getFullYear() === parseInt(parts[2]);
  }

  startPickerSelectDate(date: Date): void {
    const pad = (n: number) => String(n).padStart(2, '0');
    this.filter.startDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    this.startDateDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
    this.showStartDatePicker = false;
    this.load();
  }

  startPickerSelectToday(): void {
    this.startPickerSelectDate(new Date());
  }

  // ═══════════════════════════════════════════════════════════════
  // DATEPICKER - FILTER END DATE
  // ═══════════════════════════════════════════════════════════════

  toggleEndDatePicker(): void {
    this.showEndDatePicker = !this.showEndDatePicker;
    if (this.showEndDatePicker) {
      if (this.filter.endDate) {
        const parts = this.endDateDisplay.split('/');
        if (parts.length === 3) {
          this.endPickerCurrentDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        }
      } else {
        this.endPickerCurrentDate = new Date();
      }
      this.generateEndPickerCalendar();

      setTimeout(() => {
        if (this.endDateInput?.nativeElement) {
          const rect = this.endDateInput.nativeElement.getBoundingClientRect();
          this.endPickerTop = `${rect.bottom + 8}px`;
          this.endPickerLeft = `${rect.left}px`;
        }
      }, 50);
    }
  }

  generateEndPickerCalendar(): void {
    const year = this.endPickerCurrentDate.getFullYear();
    const month = this.endPickerCurrentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const days: Date[] = [];

    for (let i = startDay - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push(new Date(year, month + 1, d));
    }
    this.endPickerDays = days;
  }

  endPickerPreviousMonth(): void {
    this.endPickerCurrentDate = new Date(
      this.endPickerCurrentDate.getFullYear(),
      this.endPickerCurrentDate.getMonth() - 1,
      1
    );
    this.generateEndPickerCalendar();
  }

  endPickerNextMonth(): void {
    this.endPickerCurrentDate = new Date(
      this.endPickerCurrentDate.getFullYear(),
      this.endPickerCurrentDate.getMonth() + 1,
      1
    );
    this.generateEndPickerCalendar();
  }

  getEndPickerMonthYear(): string {
    return this.endPickerCurrentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  endPickerIsCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.endPickerCurrentDate.getMonth();
  }

  endPickerIsToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  endPickerIsSelected(date: Date): boolean {
    if (!this.filter.endDate) return false;
    const parts = this.endDateDisplay.split('/');
    if (parts.length !== 3) return false;
    return date.getDate() === parseInt(parts[0]) &&
           date.getMonth() === parseInt(parts[1]) - 1 &&
           date.getFullYear() === parseInt(parts[2]);
  }

  endPickerSelectDate(date: Date): void {
    const pad = (n: number) => String(n).padStart(2, '0');
    this.filter.endDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    this.endDateDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
    this.showEndDatePicker = false;
    this.load();
  }

  endPickerSelectToday(): void {
    this.endPickerSelectDate(new Date());
  }

  // ═══════════════════════════════════════════════════════════════
  // DATEPICKER - EVOLUTION DATE
  // ═══════════════════════════════════════════════════════════════

  toggleEvolutionDatePicker(): void {
    this.showEvolutionDatePicker = !this.showEvolutionDatePicker;
    if (this.showEvolutionDatePicker) {
      if (this.evolutionForm.evolutionDate) {
        const parts = this.evolutionDateDisplay.split('/');
        if (parts.length === 3) {
          this.evolutionPickerCurrentDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        }
      } else {
        this.evolutionPickerCurrentDate = new Date();
      }
      this.generateEvolutionPickerCalendar();

      setTimeout(() => {
        if (this.evolutionDateInput?.nativeElement) {
          const rect = this.evolutionDateInput.nativeElement.getBoundingClientRect();
          this.evolutionPickerTop = `${rect.bottom + 8}px`;
          this.evolutionPickerLeft = `${rect.left}px`;
        }
      }, 50);
    }
  }

  generateEvolutionPickerCalendar(): void {
    const year = this.evolutionPickerCurrentDate.getFullYear();
    const month = this.evolutionPickerCurrentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const days: Date[] = [];

    for (let i = startDay - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    const remaining = 42 - days.length;
    for (let d = 1; d <= remaining; d++) {
      days.push(new Date(year, month + 1, d));
    }
    this.evolutionPickerDays = days;
  }

  evolutionPickerPreviousMonth(): void {
    this.evolutionPickerCurrentDate = new Date(
      this.evolutionPickerCurrentDate.getFullYear(),
      this.evolutionPickerCurrentDate.getMonth() - 1,
      1
    );
    this.generateEvolutionPickerCalendar();
  }

  evolutionPickerNextMonth(): void {
    this.evolutionPickerCurrentDate = new Date(
      this.evolutionPickerCurrentDate.getFullYear(),
      this.evolutionPickerCurrentDate.getMonth() + 1,
      1
    );
    this.generateEvolutionPickerCalendar();
  }

  getEvolutionPickerMonthYear(): string {
    return this.evolutionPickerCurrentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  }

  evolutionPickerIsCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.evolutionPickerCurrentDate.getMonth();
  }

  evolutionPickerIsToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  evolutionPickerIsSelected(date: Date): boolean {
    if (!this.evolutionForm.evolutionDate) return false;
    const parts = this.evolutionDateDisplay.split('/');
    if (parts.length !== 3) return false;
    return date.getDate() === parseInt(parts[0]) &&
           date.getMonth() === parseInt(parts[1]) - 1 &&
           date.getFullYear() === parseInt(parts[2]);
  }

  evolutionPickerSelectDate(date: Date): void {
    const pad = (n: number) => String(n).padStart(2, '0');
    this.evolutionForm.evolutionDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${new Date().toTimeString().slice(0, 5)}`;
    this.evolutionDateDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
    this.showEvolutionDatePicker = false;
  }

  evolutionPickerSelectToday(): void {
    this.evolutionPickerSelectDate(new Date());
  }

  // ═══════════════════════════════════════════════════════════════
  // FORMATAÇÃO MANUAL DE DATAS
  // ═══════════════════════════════════════════════════════════════

  onStartDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    this.startDateDisplay = value;

    if (value.length === 10) {
      const parts = value.split('/');
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const year = parseInt(parts[2]);

      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const pad = (n: number) => String(n).padStart(2, '0');
        this.filter.startDate = `${year}-${pad(month)}-${pad(day)}`;
        this.load();
      }
    }
  }

  onEndDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    this.endDateDisplay = value;

    if (value.length === 10) {
      const parts = value.split('/');
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const year = parseInt(parts[2]);

      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const pad = (n: number) => String(n).padStart(2, '0');
        this.filter.endDate = `${year}-${pad(month)}-${pad(day)}`;
        this.load();
      }
    }
  }

  onEvolutionDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    this.evolutionDateDisplay = value;

    if (value.length === 10) {
      const parts = value.split('/');
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]);
      const year = parseInt(parts[2]);

      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const pad = (n: number) => String(n).padStart(2, '0');
        const currentTime = this.evolutionForm.evolutionDate?.slice(11, 16) || new Date().toTimeString().slice(0, 5);
        this.evolutionForm.evolutionDate = `${year}-${pad(month)}-${pad(day)}T${currentTime}`;
      }
    }
  }
}
